import { useMemo } from 'react';
import { useUserProfile } from './useUserProfile';
import { ROLES, normalizeRole, useUserRole } from './roles';

const SCHOOL_ACTIONS = {
  VIEW_DASHBOARD: 'view_dashboard',
  VIEW_STUDENTS: 'view_students',
  MANAGE_SUPPORT_PLANS: 'manage_support_plans',
  MANAGE_INTERVENTIONS: 'manage_interventions',
  VIEW_PROGRESS: 'view_progress',
  RECORD_CURRICULUM_ATTAINMENT: 'record_curriculum_attainment',
  VIEW_ARTICLES: 'view_articles',
  MANAGE_EHCP_UPLOAD: 'manage_ehcp_upload',
  ANALYZE_EHCP: 'analyze_ehcp',
  GENERATE_EHCNA: 'generate_ehcna',
  APPROVE_USERS: 'approve_users',
  MANAGE_GDPR: 'manage_gdpr',
  REVIEW_APDR_QUEUE: 'review_apdr_queue',
  APPROVE_APDR_QUEUE: 'approve_apdr_queue',
  MANAGE_ADMIN_RESOURCES: 'manage_admin_resources',
};

function buildCapabilitySet(role) {
  const set = new Set([
    SCHOOL_ACTIONS.VIEW_DASHBOARD,
    SCHOOL_ACTIONS.VIEW_STUDENTS,
    SCHOOL_ACTIONS.MANAGE_SUPPORT_PLANS,
    SCHOOL_ACTIONS.MANAGE_INTERVENTIONS,
    SCHOOL_ACTIONS.VIEW_PROGRESS,
    SCHOOL_ACTIONS.VIEW_ARTICLES,
    SCHOOL_ACTIONS.ANALYZE_EHCP,
    SCHOOL_ACTIONS.REVIEW_APDR_QUEUE,
  ]);

  if (role === ROLES.TEACHER || role === ROLES.SEN_CO) {
    set.add(SCHOOL_ACTIONS.RECORD_CURRICULUM_ATTAINMENT);
  }

  if (role === ROLES.SEN_CO) {
    set.add(SCHOOL_ACTIONS.MANAGE_EHCP_UPLOAD);
    set.add(SCHOOL_ACTIONS.GENERATE_EHCNA);
    set.add(SCHOOL_ACTIONS.APPROVE_USERS);
    set.add(SCHOOL_ACTIONS.MANAGE_GDPR);
    set.add(SCHOOL_ACTIONS.APPROVE_APDR_QUEUE);
  }

  if (role === ROLES.SUPER_ADMIN) {
    Object.values(SCHOOL_ACTIONS).forEach((capability) => set.add(capability));
    set.add(SCHOOL_ACTIONS.MANAGE_ADMIN_RESOURCES);
  }

  return set;
}

export function useSchoolCapabilities() {
  const { role: rawRole, loading: roleLoading } = useUserRole({ subscribe: true });
  const { profile, loading: profileLoading } = useUserProfile();

  return useMemo(() => {
    const role = normalizeRole(profile?.role || rawRole);
    const capabilities = buildCapabilitySet(role);
    return {
      loading: roleLoading || profileLoading,
      role,
      profile,
      capabilities,
      can: (action) => capabilities.has(action),
      isSENCO: role === ROLES.SEN_CO,
      isTeacher: role === ROLES.TEACHER,
      isTA: role === ROLES.TA,
      isSuperAdmin: role === ROLES.SUPER_ADMIN,
      SCHOOL_ACTIONS,
    };
  }, [profile, profileLoading, rawRole, roleLoading]);
}

export { SCHOOL_ACTIONS };
